import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
    Stack,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import {
    Autorenew as AutoRenewIcon,
    Schedule as TimeIcon,
    Payments as PaymentsIcon,
    CheckCircle as CheckCircleIcon,
    Event as EventIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ExternalLinkDialog from '../components/common/ExternalLinkDialog';
import { format } from 'date-fns';

const MembershipCard = ({ plan, isCurrentPlan, onClick }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasInitiationFee = plan.initiationFeeText !== "$0.00";
    const startDate = plan.membershipStartDate ? format(new Date(plan.membershipStartDate), 'MMM d, yyyy') : 'Not started';
    const endDate = plan.membershipEndDate ? format(new Date(plan.membershipEndDate), 'MMM d, yyyy') : 'No end date';

    return (
        <Card 
            elevation={3}
            onClick={onClick}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                position: 'relative',
                overflow: 'visible',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[6]
                },
                ...(isCurrentPlan && {
                    border: `2px solid ${theme.palette.success.main}`,
                })
            }}
        >
            {isCurrentPlan && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 5px ${theme.palette.success.light}, ${theme.shadows[3]}`,
                        zIndex: 1,
                        transform: 'rotate(-12deg)',
                        '& svg': {
                            fontSize: '1.4rem',
                            mb: 0.2
                        }
                    }}
                >
                    <CheckCircleIcon fontSize="small" />
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            lineHeight: 1
                        }}
                    >
                        Fees
                    </Typography>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            lineHeight: 1
                        }}
                    >
                        Paid
                    </Typography>
                </Box>
            )}
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography 
                    variant="h5" 
                    component="h2" 
                    gutterBottom 
                    color="primary"
                    sx={{ 
                        fontWeight: 'bold',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        pb: 1,
                        pr: isCurrentPlan ? 5 : 0
                    }}
                >
                    {plan.membershipType}
                </Typography>

                <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ 
                        mb: 3,
                        minHeight: '80px',
                        overflowY: 'auto'
                    }}
                >
                    {plan.description}
                </Typography>

                <Stack spacing={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <EventIcon color="action" />
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Start Date: {startDate}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                End Date: {endDate}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider />

                    <Box display="flex" alignItems="center" gap={1}>
                        <TimeIcon color="action" />
                        <Typography variant="body1">
                            {plan.membershipTermLength}
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="flex-start" gap={1}>
                        <PaymentsIcon color="action" sx={{ mt: 0.5 }} />
                        <Box>
                            <Typography variant="body1">
                                Term Fee: {plan.membershipTermFee}
                            </Typography>
                            {hasInitiationFee && (
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    + {plan.initiationFeeText} initiation fee
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <AutoRenewIcon 
                            color={plan.membershipAutoRenew ? "success" : "error"} 
                        />
                        <Typography variant="body1">
                            {plan.membershipAutoRenew ? "Auto-renews" : "Does not auto-renew"}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const Memberships = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [memberships, setMemberships] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [coachingDialogOpen, setCoachingDialogOpen] = useState(false);
    const [coachingConfirmDialogOpen, setCoachingConfirmDialogOpen] = useState(false);
    const [coachingSuccessDialogOpen, setCoachingSuccessDialogOpen] = useState(false);
    const [coachingLoading, setCoachingLoading] = useState(false);
    const [existingMemberDialogOpen, setExistingMemberDialogOpen] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [childrenData, setChildrenData] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();

    // Get coaching/playing status from our database
    const isCoachingOrPlaying = useMemo(() => {
        if (!clientData) return false;
        return clientData.isCoachingOrPlaying || false;
    }, [clientData]);

    // Fetch client data from our database
    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const response = await api.get('/users/dashboard');
                const { client, children } = response.data.data;
                setClientData(client);
                setChildrenData(children || []);
            } catch (error) {
                setError('Failed to fetch client data');
            }
        };

        if (user?.ssUsername) {
            fetchClientData();
        }
    }, [user?.ssUsername]);

    const checkMembershipValidity = useCallback(() => {
        const membershipType = user?.membershipType?.toLowerCase() || '';
        const numChildren = childrenData.length;
        const requiredCount = numChildren === 0 ? 1 : (isCoachingOrPlaying ? numChildren + 1 : numChildren);

        // Basic checks for membership existence
        if (!user?.membershipNumber || !user?.membershipType || !user?.membershipClassification) {
            return {
                isValid: false,
                paidCount: 0,
                requiredCount: requiredCount
            };
        }

        // Check if membership is active (end date must exist and be in the future)
        if (!user?.membershipEndDate || new Date(user.membershipEndDate) < new Date()) {
            return {
                isValid: false,
                paidCount: 0,
                requiredCount: requiredCount
            };
        }

        // If no children, need one membership for self
        if (numChildren === 0) {
            if (!membershipType.includes('coach') && !membershipType.includes('player')) {
                return {
                    isValid: false,
                    paidCount: 0,
                    requiredCount: requiredCount
                };
            }
        } else {
            // With children, need player memberships matching total required count
            if (membershipType.includes('player')) {
                // Extract the number of players from the membership type (e.g., "3 Players (2025)" -> 3)
                const playerCount = parseInt(membershipType.match(/^(\d+)\s*player/)?.[1] || 1);
                
                if (playerCount < requiredCount) {
                    return {
                        isValid: false,
                        paidCount: playerCount,
                        requiredCount: requiredCount
                    };
                }
            } else {
                return {
                    isValid: false,
                    paidCount: 0,
                    requiredCount: requiredCount
                };
            }
        }

        return {
            isValid: true,
            paidCount: requiredCount,
            requiredCount: requiredCount
        };
    }, [user, childrenData.length, isCoachingOrPlaying]);

    const membershipStatus = checkMembershipValidity();

    // Only fetch plans if membership is not valid
    useEffect(() => {
        if (!user) return; // Wait for user data to be available
        
        const status = checkMembershipValidity();
        if (status.isValid) return; // Don't fetch if membership is valid
        
        const fetchData = async () => {
            try {
                setLoading(true);
                const membershipsResponse = await api.get('/users/memberships/plans');
                setMemberships(membershipsResponse.data || []);
            } catch (error) {
                setError('Failed to load membership plans. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user, checkMembershipValidity]); // Include dependencies that affect membership validity

    // Show coaching dialog on component mount if not set
    useEffect(() => {
        const hasChildren = childrenData.length > 0;
        if (hasChildren && !isCoachingOrPlaying && !sessionStorage.getItem('coachingDialogShown')) {
            setCoachingDialogOpen(true);
            sessionStorage.setItem('coachingDialogShown', 'true');
        }
    }, [isCoachingOrPlaying, childrenData.length]);

    // Clear the session storage when component unmounts
    useEffect(() => {
        return () => {
            sessionStorage.removeItem('coachingDialogShown');
        };
    }, []);

    const handleSetCoachingPlaying = async () => {
        try {
            setCoachingLoading(true);
            setError(null);

            // Update coaching status in our database
            const response = await api.put('/users/client/status', {
                isCoachingOrPlaying: true
            });

            // Refresh client data
            const dashboardResponse = await api.get('/users/dashboard');
            const { client, children } = dashboardResponse.data.data;
            setClientData(client);
            setChildrenData(children || []);
            
            setCoachingConfirmDialogOpen(false);
            setCoachingSuccessDialogOpen(true);
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred');
        } finally {
            setCoachingLoading(false);
        }
    };

    const isCurrentPlan = (plan) => {
        return user?.membershipType === plan.membershipType &&
               user?.membershipEndDate &&
               new Date(user.membershipEndDate) > new Date();
    };

    const handleMembershipClick = () => {
        // If user has an active membership, show the call us dialog
        if (user?.membershipEndDate && new Date(user.membershipEndDate) > new Date()) {
            setExistingMemberDialogOpen(true);
            return;
        }

        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setExistingMemberDialogOpen(false);
    };

    const handleDialogConfirm = () => {
        window.open('https://totalturf.ezfacility.com/Membership', '_blank', 'noopener,noreferrer');
        setDialogOpen(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">
                    Administrative Fees
                </Typography>
            </Box>

            <Alert 
                severity={membershipStatus.isValid ? "success" : "warning"}
                sx={{ mb: 3 }}
            >
                {membershipStatus.isValid ? (
                    <>
                        <strong>All Set!</strong> Your administrative fees are paid and up to date. {
                            childrenData.length === 0 
                                ? "You have paid for yourself."
                                : isCoachingOrPlaying
                                    ? `You have paid for ${membershipStatus.requiredCount} people (${childrenData.length} ${childrenData.length === 1 ? 'child' : 'children'} plus yourself as a coach/player).`
                                    : `You have paid for your ${childrenData.length} ${childrenData.length === 1 ? 'child' : 'children'}.`
                        }
                        {membershipStatus.paidCount > membershipStatus.requiredCount && 
                            ` You have ${membershipStatus.paidCount} slots paid for ${membershipStatus.requiredCount} required.`}
                    </>
                ) : (
                    <>
                        <strong>Action Required:</strong> {
                            childrenData.length === 0 
                                ? "You need to pay administrative fees for yourself."
                                : isCoachingOrPlaying
                                    ? `You need to pay administrative fees for ${membershipStatus.requiredCount} people (${childrenData.length} ${childrenData.length === 1 ? 'child' : 'children'} plus yourself as a coach/player).`
                                    : `You need to pay administrative fees for your ${childrenData.length} ${childrenData.length === 1 ? 'child' : 'children'}.`
                        }
                        {childrenData.length > 0 && !isCoachingOrPlaying && (
                            <> If you also coach or play at TTE, please click the "I Coach/Play" button on your profile to add yourself.</>
                        )}
                    </>
                )}
            </Alert>

            {membershipStatus.isValid ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    To modify your existing administrative fees or change the number of players, please contact TTE. Our staff will be happy to assist you with any changes to your account.
                </Alert>
            ) : memberships.length === 0 ? (
                <Alert severity="info">
                    No membership plans are currently available.
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {memberships.map((plan, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                            <MembershipCard
                                plan={plan}
                                isCurrentPlan={isCurrentPlan(plan)}
                                onClick={() => handleMembershipClick(plan)}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Coaching Question Dialog */}
            <Dialog
                open={coachingDialogOpen}
                onClose={() => setCoachingDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Do you coach or play at Total Turf Experience?
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        By confirming that you coach or play at Total Turf Experience, you will be required to pay administrative fees in addition to your children. Do not select this option if you will not be participating with your children. Do you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setCoachingDialogOpen(false)}
                        color="primary"
                    >
                        No, just my children
                    </Button>
                    <Button 
                        onClick={() => {
                            setCoachingDialogOpen(false);
                            setCoachingConfirmDialogOpen(true);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Yes, I coach/play
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Coaching Confirmation Dialog */}
            <Dialog
                open={coachingConfirmDialogOpen}
                onClose={() => setCoachingConfirmDialogOpen(false)}
            >
                <DialogTitle>
                    Confirm Coaching/Playing Status
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        By confirming that you coach or play at Total Turf Experience, you will be required to pay administrative fees in addition to your children. Do not select this option if you will not be participating with your children. Do you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setCoachingConfirmDialogOpen(false)} 
                        color="primary"
                        disabled={coachingLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSetCoachingPlaying} 
                        color="primary" 
                        variant="contained"
                        disabled={coachingLoading}
                    >
                        {coachingLoading ? <CircularProgress size={24} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={coachingSuccessDialogOpen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: 'success.main' }}>
                    Successfully Updated!
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your coaching/playing status has been updated. You now need administrative fees for {childrenData.length + 1} people:
                        <Box component="ul" sx={{ mt: 1, mb: 1 }}>
                            <li>{childrenData.length} {childrenData.length === 1 ? 'child' : 'children'}</li>
                            <li>Yourself as a coach/player</li>
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setCoachingSuccessDialogOpen(false)} 
                        color="primary"
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for existing members */}
            <Dialog
                open={existingMemberDialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Contact Us to Modify Administrative Fees
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        To modify your existing administrative fees or change the number of players, please contact TTE. Our staff will be happy to assist you with any changes to your account.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Your existing dialogs */}
            <ExternalLinkDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onConfirm={handleDialogConfirm}
                title="Purchase Administrative Fees"
                message="You will be redirected to Total Turf Experience's website to complete your purchase. Would you like to continue?"
                continueButtonText="Continue to Purchase"
            />
        </Box>
    );
};

export default Memberships; 